import type {
  ActiveWork,
  Device,
  Ended,
  Job,
  JobListEntry,
  JobStatus,
  LiveActivity,
  Location,
  ManagedTaskType,
  ManagedEmployee,
  ManagedDevice,
  EnrolCode,
  Paused,
  ScanResult,
  Role,
  Session,
  StandingTask,
  TaskType,
  Employee,
  EmployeeDay,
  ActivityVisibility,
  TaskVisibility,
} from "./types.js";

/**
 * Everything the server can say that the UI has to act on differently.
 * A 401 is not one thing: an unregistered device means this tablet is
 * not enrolled, an unauthenticated one means the shift cookie lapsed and
 * the employee just needs to tap their name again.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly retryAfterSeconds: number | undefined;
  /** Where the app went, when the code is `moved`. An https origin or nothing. */
  readonly movedTo: string | undefined;

  constructor(status: number, code: string, retryAfterSeconds?: number, movedTo?: string) {
    super(code);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
    this.movedTo = movedTo;
  }

  get deviceUnregistered(): boolean {
    return this.status === 401 && this.code === "device_unregistered";
  }

  get sessionLapsed(): boolean {
    return this.status === 401 && this.code === "unauthenticated";
  }

  get moved(): boolean {
    return this.status === 410 && this.code === "moved";
  }
}

/**
 * The address a `moved` answer names, reduced to its origin, or nothing.
 * Only https: the link is rendered for somebody to tap, and a page that
 * can be told where to send people should not be told to send them to a
 * `javascript:` URL.
 */
function movedOrigin(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  try {
    const url = new URL(raw);
    return url.protocol === "https:" ? url.origin : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Told once, by App, so a shift cookie that expires mid-screen sends the
 * employee to the PIN pad.
 *
 * Without this, `sessionLapsed` was only ever read by the startup check.
 * Once inside the app a lapsed session was just a 401 that each panel
 * turned into its own generic message — "Could not make a code", "Could
 * not load the people" — while the screen carried on showing data from
 * before it expired. Every button failing for a reason none of them
 * names is a much worse experience than being asked for a PIN, and it
 * looks like the app is broken rather than like a shift ending.
 */
let onSessionLapsed: (() => void) | null = null;
let onDeviceLost: (() => void) | null = null;

export function whenSessionLapses(handler: () => void): void {
  onSessionLapsed = handler;
}

/**
 * The other 401, and it needs the other screen.
 *
 * A device can stop being this device while somebody is looking at it:
 * revoked by an admin, or — the way it actually happened on
 * 2026-09-07 — re-keyed because an enrolment code for it was redeemed
 * somewhere else. Sending them to the PIN pad would be wrong, because no
 * PIN will help; they need a token or a code. Without this the app just
 * kept polling a dead cookie every thirty seconds and every button
 * failed with whatever its own screen happened to say.
 */
export function whenDeviceLost(handler: () => void): void {
  onDeviceLost = handler;
}

/**
 * The app has a new address, and this page is still open on the old one.
 *
 * After the move (MIGRATION 1.11, 7.7) the old hostname redirects pages
 * and answers `/api/*` with 410 `moved` — an API call cannot follow a
 * redirect to another origin with its cookies. A tablet left open
 * overnight has the old app in memory, so without this every tap is a
 * red "Could not …" that says nothing about where to go. No PIN or
 * enrolment on this address can help either; only the new one can.
 */
let onMoved: ((to: string | undefined) => void) | null = null;

export function whenMoved(handler: (to: string | undefined) => void): void {
  onMoved = handler;
}

// Omit, not intersect: RequestInit already declares body as BodyInit, and
// an intersection keeps the narrower type rather than widening it.
async function request<T>(
  path: string,
  init: Omit<RequestInit, "body"> & { body?: unknown } = {},
): Promise<T> {
  const { body, ...rest } = init;
  const res = await fetch(path, {
    ...rest,
    credentials: "same-origin",
    headers: {
      ...(body === undefined ? {} : { "content-type": "application/json" }),
      ...rest.headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (res.ok) {
    return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
  }

  let code = `http_${res.status}`;
  let retryAfter: number | undefined;
  let movedTo: string | undefined;
  try {
    const payload = (await res.json()) as {
      error?: string;
      retryAfterSeconds?: number;
      to?: unknown;
    };
    if (payload.error) code = payload.error;
    retryAfter = payload.retryAfterSeconds;
    movedTo = movedOrigin(payload.to);
  } catch {
    // A non-JSON error body is still an error; the status carries it.
  }
  const error = new ApiError(res.status, code, retryAfter, movedTo);
  // Every call goes through here, so this is the one place that cannot
  // be forgotten by a screen added later.
  if (error.sessionLapsed) onSessionLapsed?.();
  if (error.deviceUnregistered) onDeviceLost?.();
  if (error.moved) onMoved?.(error.movedTo);
  throw error;
}

export const api = {
  me: () => request<{ employee: Employee; device: Device }>("/api/auth/me"),

  employees: () => request<{ employees: Employee[] }>("/api/auth/employees"),

  login: (employeeId: string, pin: string) =>
    request<{ employee: Employee; expiresInSeconds: number }>("/api/auth/login", {
      method: "POST",
      body: { employeeId, pin },
    }),

  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  /**
   * The QR path. The code came off a camera into the URL fragment, so it
   * has never been near the server's logs.
   */
  enrolWithCode: (code: string) =>
    request<{ device: Device }>("/api/devices/enrol-code", {
      method: "POST",
      body: { code },
    }),

  adminDevices: () => request<{ devices: ManagedDevice[] }>("/api/admin/devices"),

  addDevice: (name: string, sessionMinutes?: number) =>
    request<{ device: ManagedDevice; token: string }>("/api/admin/devices", {
      method: "POST",
      body: { name, ...(sessionMinutes ? { sessionMinutes } : {}) },
    }),

  enrolCode: (deviceId: string) =>
    request<EnrolCode>(
      `/api/admin/devices/${encodeURIComponent(deviceId)}/enrol-code`,
      { method: "POST" },
    ),

  setDeviceSession: (deviceId: string, sessionMinutes: number) =>
    request<{ device: ManagedDevice; appliesAtNextSignIn: boolean }>(
      `/api/admin/devices/${encodeURIComponent(deviceId)}`,
      { method: "PATCH", body: { sessionMinutes } },
    ),

  revokeDevice: (deviceId: string) =>
    request<{ device: ManagedDevice }>(
      `/api/admin/devices/${encodeURIComponent(deviceId)}/revoke`,
      { method: "POST" },
    ),

  enrol: (token: string) =>
    request<{ device: Device }>("/api/devices/enrol", {
      method: "POST",
      body: { token },
    }),

  scan: (label: string) =>
    request<ScanResult>(`/api/jobs/by-label/${encodeURIComponent(label)}`),

  activitiesFor: (employeeId: string) =>
    request<{
      employee: { id: string; displayName: string };
      activities: ActivityVisibility[];
    }>(`/api/manage/employees/${employeeId}/activities`),

  setActivities: (employeeId: string, hidden: string[]) =>
    request<{ activities: ActivityVisibility[] }>(
      `/api/manage/employees/${employeeId}/activities`,
      { method: "PUT", body: { hidden } },
    ),

  taskVisibility: (taskTypeId: string) =>
    request<{ people: TaskVisibility[] }>(
      `/api/manage/task-types/${taskTypeId}/visibility`,
    ),

  setTaskVisibility: (taskTypeId: string, hidden: string[]) =>
    request<{ people: TaskVisibility[] }>(
      `/api/manage/task-types/${taskTypeId}/visibility`,
      { method: "PUT", body: { hidden } },
    ),

  /** `was` asks how that session ended, for a screen that was showing it. */
  currentSession: (was?: string) =>
    request<{
      session: Session | null;
      active: ActiveWork | null;
      paused: Paused;
      ended?: Ended | null;
    }>(`/api/sessions/current${was ? `?was=${encodeURIComponent(was)}` : ""}`),

  standing: () => request<{ standing: StandingTask[] }>("/api/standing"),

  start: (jobId: string, source: "APP" | "SCAN") =>
    request<{
      session: Session;
      alreadyOpen: boolean;
      /** Set when starting this closed a different running task. */
      autoClosedSessionId: string | null;
      /** That task had run past the limit and was discarded, not counted. */
      autoClosedStale: boolean;
    }>("/api/sessions/start", {
      method: "POST",
      body: { jobId, source },
    }),

  // Pause and stop are the same call. What differs is the screen the
  // employee comes back to, and whether the gap reads as a break.
  /** `stale` means the timer had run past the limit and was discarded. */
  stop: (reason: "PAUSED" | "STOPPED" = "STOPPED", jobId?: string) =>
    request<{ session: Session; stale: boolean }>("/api/sessions/stop", {
      method: "POST",
      body: jobId ? { jobId, reason } : { reason },
    }),

  taskTypes: () => request<{ taskTypes: TaskType[] }>("/api/task-types"),
  locations: () => request<{ locations: Location[] }>("/api/locations"),

  jobs: (status?: JobStatus) =>
    request<{ jobs: JobListEntry[] }>(
      `/api/jobs${status ? `?status=${status}` : ""}`,
    ),

  createJob: (taskTypeId: string, locationId?: string, invoiceNumbers?: string[]) =>
    request<{ job: Job }>("/api/jobs", {
      method: "POST",
      body: {
        taskTypeId,
        ...(locationId ? { locationId } : {}),
        ...(invoiceNumbers?.length ? { invoiceNumbers } : {}),
      },
    }),

  dashboardLive: () =>
    request<{ asOf: string; activities: LiveActivity[] }>("/api/dashboard/live"),

  setJobInvoices: (jobId: string, invoiceNumbers: string[]) =>
    request<{ invoices: string[] }>(
      `/api/jobs/${encodeURIComponent(jobId)}/invoices`,
      { method: "PUT", body: { invoiceNumbers } },
    ),

  // Managing the catalogue and the people. Leads and admins only.
  manageTaskTypes: () =>
    request<{ taskTypes: ManagedTaskType[] }>("/api/manage/task-types"),

  addTaskType: (name: string, kind: "JOB" | "STANDING", description?: string) =>
    request<{ taskType: ManagedTaskType; revived: boolean }>(
      "/api/manage/task-types",
      { method: "POST", body: { name, kind, ...(description ? { description } : {}) } },
    ),

  updateTaskType: (
    id: string,
    changes: { name?: string; description?: string | null; active?: boolean },
  ) =>
    request<{ taskType: ManagedTaskType }>(
      `/api/manage/task-types/${encodeURIComponent(id)}`,
      { method: "PATCH", body: changes },
    ),

  manageEmployees: () => request<{ employees: ManagedEmployee[] }>("/api/manage/employees"),

  addEmployee: (displayName: string, role: Role) =>
    request<{ employee: ManagedEmployee; pin: string }>("/api/manage/employees", {
      method: "POST",
      body: { displayName, role },
    }),

  updateEmployee: (
    id: string,
    changes: {
      displayName?: string;
      role?: Role;
      active?: boolean;
      resetPin?: boolean;
    },
  ) =>
    request<{ employee: ManagedEmployee; pin?: string }>(
      `/api/manage/employees/${encodeURIComponent(id)}`,
      { method: "PATCH", body: changes },
    ),

  // Admin only. See the note on the dashboard routes.
  dashboardDays: (month: string) =>
    request<{ month: string; days: string[] }>(
      `/api/dashboard/days?month=${encodeURIComponent(month)}`,
    ),

  dashboardDay: (date: string) =>
    request<{ date: string; employees: EmployeeDay[] }>(
      `/api/dashboard/day?date=${encodeURIComponent(date)}`,
    ),

  completeJob: (jobId: string) =>
    request<{ job: Job; closedSessions: number }>(
      `/api/jobs/${encodeURIComponent(jobId)}/complete`,
      { method: "POST" },
    ),
};
